/* ABD v10 · Shared promotions + per-browser, per-product repeating visitor countdown. */
(() => {
  'use strict';
  const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})$/i;
  const HOUR = 3600000;
  const MINUTE = 60000;
  const pad = n => String(n).padStart(2, '0');
  function readInstant(text, required = false) {
    const raw = (text || '').trim();
    if (!raw && !required) return null;
    if (!ISO.test(raw)) return NaN;
    const time = Date.parse(raw);
    return Number.isFinite(time) ? time : NaN;
  }
  function isValidInstant(t) { return t !== null && Number.isFinite(t); }
  function numberWithin(value, min, max) {
    const n = Number(value);
    return Number.isInteger(n) && n >= min && n <= max ? n : NaN;
  }
  function options(root) {
    const d = root.dataset;
    const mode = d.mode === 'visitor' ? 'visitor' : (d.mode === 'repeat' ? 'repeat' : 'fixed');
    // Ignore scheduled-promotion dates in per-visitor mode.
    if (mode === 'visitor') {
      const hours = numberWithin(d.visitorHours, 0, 24);
      const minutes = numberWithin(d.visitorMinutes, 0, 59);
      const total = hours * 60 + minutes;
      if (!Number.isFinite(total) || total < 1 || total > 1440) {
        return {error:'Per-visitor timer must be between 00:01 and 24:00. Use 00 minutes with 24 hours.'};
      }
      return {mode, duration:total * MINUTE};
    }
    const start = readInstant(d.startAt);
    const end = readInstant(d.endAt, mode === 'fixed');
    const stop = readInstant(d.stopAt, mode === 'repeat');
    const hours = numberWithin(d.repeatHours, 1, 168);
    const minutes = numberWithin(d.durationMinutes, 1, 10079);
    if (start !== null && !Number.isFinite(start)) return {error:'Use a valid start date with a timezone offset.'};
    if (mode === 'fixed') {
      if (!isValidInstant(end)) return {error:'Set a valid one-time end date with a timezone offset.'};
      if (start !== null && start >= end) return {error:'Start must be earlier than the end date.'};
      return {mode, start, end};
    }
    if (!isValidInstant(start) || !isValidInstant(stop)) return {error:'Repeating mode requires both start and final stop dates with timezone offsets.'};
    if (stop <= start) return {error:'The final stop date must be later than the start date.'};
    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes * MINUTE >= hours * HOUR) return {error:'Repeat hours must be 1–168; duration must be shorter than the repeat interval.'};
    return {mode, start, stop, interval:hours * HOUR, duration:minutes * MINUTE};
  }
  function schedule(now, config) {
    if (config.error) return {kind:'invalid'};
    if (config.mode === 'fixed') {
      if (config.start !== null && now < config.start) return {kind:'upcoming', until:config.start};
      if (now >= config.end) return {kind:'expired'};
      return {kind:'active', until:config.end};
    }
    if (now < config.start) return {kind:'upcoming', until:config.start};
    if (now >= config.stop) return {kind:'expired'};
    const cycles = Math.floor((now - config.start) / config.interval);
    const windowStart = config.start + cycles * config.interval;
    const windowEnd = Math.min(windowStart + config.duration, config.stop);
    if (now < windowEnd) return {kind:'active', until:windowEnd};
    const nextStart = windowStart + config.interval;
    if (nextStart >= config.stop) return {kind:'expired'};
    return {kind:'upcoming', until:nextStart};
  }
  // The first visit for each product in this browser determines the rolling cycle.
  // The timestamp is not customer identity data and is never sent to the server.
  function visitorStart(root, now) {
    const key = `abd-countdown:v10:product:${root.dataset.productId || 'default'}`;
    let anchor = now;
    try {
      const existing = Number(window.localStorage.getItem(key));
      if (Number.isSafeInteger(existing) && existing > 0 && existing <= now) anchor = existing;
      else window.localStorage.setItem(key, String(anchor));
    } catch (_) { /* Storage blocked: the open page still counts down and loops. */ }
    return anchor;
  }
  function visitorSchedule(now, config, anchor) {
    const elapsed = Math.max(0, now - anchor);
    const cycles = Math.floor(elapsed / config.duration);
    return {kind:'active', until:anchor + (cycles + 1) * config.duration};
  }
  function timeString(milliseconds, daysEnabled, secondsEnabled, format = 'compact') {
    let remaining = Math.max(0, secondsEnabled ? Math.ceil(milliseconds / 1000) : Math.ceil(milliseconds / 60000) * 60);
    const days = Math.floor(remaining / 86400);
    let hours = Math.floor((remaining % 86400) / 3600);
    if (!daysEnabled) hours += days * 24;
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;
    const compact = format === 'compact' && days === 0 && hours === 0;
    const part = compact ? (secondsEnabled ? `${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)} min`) : (secondsEnabled ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(hours)}:${pad(minutes)}`);
    return daysEnabled && days > 0 ? `${days}d ${part}` : part;
  }
  function initialize(root) {
    if (root.dataset.abdCountdownReady === 'true') return;
    root.dataset.abdCountdownReady = 'true';
    const config = options(root);
    const label = root.querySelector('[data-abd-countdown-label]');
    const clock = root.querySelector('[data-abd-countdown-clock]');
    const warning = root.parentElement && root.parentElement.querySelector('[data-abd-countdown-warning]');
    if (!label || !clock) return;
    if (config.error) {
      root.hidden = true;
      if (warning && root.dataset.shopifyEditor === 'true') {
        warning.textContent = `Countdown configuration: ${config.error}`;
        warning.hidden = false;
      }
      return;
    }
    let ticker;
    let finished = false;
    let anchor = config.mode === 'visitor' ? visitorStart(root, Date.now()) : null;
    function update() {
      if (!root.isConnected) { clearInterval(ticker); document.removeEventListener('visibilitychange', onVisibility); return; }
      const now = Date.now();
      // If the device clock moves backwards, start a fresh sensible cycle.
      if (config.mode === 'visitor' && now < anchor) anchor = visitorStart(root, now);
      const state = config.mode === 'visitor' ? visitorSchedule(now, config, anchor) : schedule(now, config);
      root.classList.toggle('is-ended', state.kind === 'expired');
      if (state.kind === 'expired') {
        if (root.dataset.expiredBehavior === 'message') {
          label.textContent = root.dataset.labelExpired || 'This offer has ended';
          clock.textContent = '';
          root.hidden = false;
        } else root.hidden = true;
        finished = true;
        clearInterval(ticker);
        document.removeEventListener('visibilitychange', onVisibility);
        return;
      }
      if (state.kind === 'upcoming' && root.dataset.inactiveBehavior !== 'next') {
        root.hidden = true;
        return;
      }
      label.textContent = config.mode === 'visitor'
        ? (root.dataset.labelVisitor || 'Offer refreshes in')
        : (state.kind === 'active' ? (root.dataset.labelActive || 'Hurry! Offer expires in') : (root.dataset.labelNext || 'Next offer starts in'));
      // Visitor timers show 24:00:00, not "1d 00:00:00", for the 24-hour setting.
      clock.textContent = timeString(state.until - now, config.mode !== 'visitor' && root.dataset.showDays !== 'false', root.dataset.showSeconds !== 'false', root.dataset.timeFormat || 'compact');
      root.hidden = false;
    }
    function onVisibility() { if (!document.hidden) update(); }
    update();
    if (!finished) {
      ticker = window.setInterval(update, 1000);
      document.addEventListener('visibilitychange', onVisibility);
    }
  }
  function scan(scope = document) { scope.querySelectorAll('[data-abd-countdown]').forEach(initialize); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();
  document.addEventListener('shopify:section:load', evt => scan(evt.target || document));
  // Export only when manually opted in during local tests. Not required by the storefront.
  if (typeof window !== 'undefined' && window.__ABD_COUNTDOWN_TEST__) window.__ABD_COUNTDOWN_TEST__ = {readInstant, options, schedule, visitorSchedule, visitorStart, timeString};
})();
